import * as THREE from "three";

/**
 * Bridges the three coordinate systems involved in try-on:
 *
 *   1. MediaPipe returns landmarks normalized 0..1 against the *source* frame.
 *   2. The source is painted into a container with `object-fit: cover`, which
 *      scales it up and crops the overflow.
 *   3. three.js needs world units centred on the container.
 *
 * Getting this wrong is the single most common cause of glasses that look
 * right in a square window and slide off the face in a wide one, so the whole
 * mapping lives here as pure functions with no React or WebGL involvement.
 *
 * @param {object} input
 * @param {number} input.sourceWidth   Intrinsic width of the video MediaPipe analysed.
 * @param {number} input.sourceHeight  Intrinsic height of the same.
 * @param {number} input.containerWidth   On-screen width of the element showing it.
 * @param {number} input.containerHeight  On-screen height of the same.
 * @param {boolean} input.mirrored  True for the webcam, shown flipped so it behaves like a mirror.
 * @param {"cover"|"contain"} input.fit  Must match the CSS `object-fit` on that element.
 *   `cover` fills the container and crops the overflow — right for the webcam,
 *   where a full-bleed frame looks like a mirror. `contain` letterboxes so the
 *   whole source stays visible.
 */
export function createFitMapping({
  sourceWidth,
  sourceHeight,
  containerWidth,
  containerHeight,
  mirrored,
  fit,
}) {
  // Both modes scale uniformly and centre; they differ only in which axis
  // wins. `cover` takes the larger scale so the source overflows and is
  // cropped; `contain` takes the smaller so it fits with bars either side.
  const scaleX = containerWidth / sourceWidth;
  const scaleY = containerHeight / sourceHeight;
  const scale = fit === "cover" ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);

  const displayedWidth = sourceWidth * scale;
  const displayedHeight = sourceHeight * scale;

  // Negative when cropping (cover), positive when letterboxing (contain).
  const offsetX = (containerWidth - displayedWidth) / 2;
  const offsetY = (containerHeight - displayedHeight) / 2;

  const normX = (landmark) => (mirrored ? 1 - landmark.x : landmark.x);

  return {
    /** Size the source is actually painted at, after cover-scaling. */
    displayedWidth,
    displayedHeight,
    mirrored,

    /** Landmark -> container pixel coords, origin top-left. */
    toPixel(landmark) {
      return {
        x: offsetX + normX(landmark) * displayedWidth,
        y: offsetY + landmark.y * displayedHeight,
      };
    },

    /** Landmark -> three.js world coords, origin at container centre, +Y up. */
    toWorld(landmark, target = new THREE.Vector3()) {
      const px = offsetX + normX(landmark) * displayedWidth;
      const py = offsetY + landmark.y * displayedHeight;

      return target.set(
        px - containerWidth / 2,
        // Image Y grows downward, three.js Y grows upward.
        containerHeight / 2 - py,
        // MediaPipe's z shares roughly the same scale as x, so it scales by
        // width. Its sign is inverted: smaller z means nearer the camera,
        // whereas in three.js nearer means larger z.
        -landmark.z * displayedWidth,
      );
    },
  };
}

/**
 * Camera rig that makes the z=0 plane map 1:1 onto container pixels.
 *
 * With the camera this far back and this vertical FOV, a point at z=0 lands on
 * exactly the pixel its landmark came from, while geometry off that plane —
 * the temples running back past the ears — still gets true perspective. An
 * orthographic camera would give the same 2D guarantee but render the temples
 * flat.
 */
export function getCameraRig(containerHeight) {
  const distance = containerHeight * 1.2;
  const fov = 2 * Math.atan(containerHeight / 2 / distance) * (180 / Math.PI);
  return { distance, fov };
}
